import { type RedeemOption } from "@/hooks/useRewards";

interface Props {
  option: RedeemOption;
  currentPoints: number;
  onSelect: (option: RedeemOption) => void;
}

const TIER_COLORS: Record<number, { bg: string; border: string; badge: string; badgeText: string; icon: string; glow: string }> = {
  50:  { bg: "from-gray-800/60 to-gray-900/60",   border: "border-white/10",        badge: "bg-gray-700 text-gray-300",       badgeText: "مبتدئ",   icon: "ri-medal-line",        glow: "" },
  100: { bg: "from-amber-900/30 to-gray-900/60",  border: "border-amber-700/30",    badge: "bg-amber-700/30 text-amber-500",  badgeText: "شائع",    icon: "ri-medal-2-line",      glow: "hover:border-amber-700/50" },
  200: { bg: "from-amber-800/30 to-gray-900/60",  border: "border-amber-500/30",    badge: "bg-amber-500/20 text-amber-400",  badgeText: "الأفضل",  icon: "ri-vip-crown-line",    glow: "hover:border-amber-500/50" },
  300: { bg: "from-cyan-900/30 to-gray-900/60",   border: "border-cyan-400/30",     badge: "bg-cyan-400/20 text-cyan-300",    badgeText: "بلاتيني", icon: "ri-vip-diamond-line",  glow: "hover:border-cyan-400/50" },
};

export default function RedeemOptionCard({ option, currentPoints, onSelect }: Props) {
  const canAfford = currentPoints >= option.points;
  const cfg = TIER_COLORS[option.points] ?? TIER_COLORS[50];
  const shortage = option.points - currentPoints;

  return (
    <button
      onClick={() => onSelect(option)}
      disabled={!canAfford}
      className={`w-full text-right bg-gradient-to-br ${cfg.bg} border ${cfg.border} ${cfg.glow} rounded-2xl p-5 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed relative overflow-hidden group`}
    >
      {/* Top accent */}
      {canAfford && (
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400/0 via-amber-400/40 to-amber-400/0 opacity-0 group-hover:opacity-100 transition-opacity"></div>
      )}

      <div className="flex items-center justify-between gap-4">
        {/* Left: icon + info */}
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 ${canAfford ? "bg-amber-400/15 border border-amber-400/25" : "bg-white/5 border border-white/10"}`}>
            <i className={`${cfg.icon} text-xl ${canAfford ? "text-amber-400" : "text-gray-600"}`}></i>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className={`text-base font-black ${canAfford ? "text-white" : "text-gray-600"}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                {option.label}
              </p>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.badge}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                {cfg.badgeText}
              </span>
            </div>
            <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {option.description}
            </p>
            {!canAfford && (
              <p className="text-red-400/70 text-[11px] mt-1 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-lock-line text-xs"></i>
                تحتاج {shortage} نقطة إضافية
              </p>
            )}
          </div>
        </div>

        {/* Right: points cost + arrow */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-left">
            <p className={`text-xl font-black ${canAfford ? "text-amber-400" : "text-gray-600"}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {option.points}
            </p>
            <p className="text-gray-600 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة</p>
          </div>
          <div className={`w-8 h-8 flex items-center justify-center rounded-xl transition-all ${canAfford ? "bg-amber-400/15 text-amber-400 group-hover:bg-amber-400 group-hover:text-black" : "bg-white/5 text-gray-700"}`}>
            <i className="ri-arrow-left-line text-sm"></i>
          </div>
        </div>
      </div>

      {/* Progress bar showing how close they are */}
      {!canAfford && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-gray-600 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>تقدمك نحو هذا الخيار</span>
            <span className="text-gray-500 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {currentPoints} / {option.points}
            </span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-700/50 rounded-full transition-all"
              style={{ width: `${Math.min(100, (currentPoints / option.points) * 100)}%` }}
            ></div>
          </div>
        </div>
      )}
    </button>
  );
}
